import { useMemo } from "react";
import { TokenCell } from "../../../components/grid/cells/TokenCell";
import { Toolbar } from "../../../components/Toolbar";
import { formatCentsAsDollars } from "../../../util/formatters";
import { AdministrateBackgroundExportButton } from "../../components/AdministrateBackgroundExportButton";
import { AdministrateEnumFilter } from "../../components/AdministrateEnumFilter";
import { AdministrateTable } from "../../components/AdministrateTable";
import { SystemConfigLayout } from "../../components/SystemConfigLayout";
import { DESTROY, makeColumns, UPDATE, useAdministrateTable, useCreatePermission } from "../../utils";
import { CreateNonCoveredPricingModal } from "../components/CreateNonCoveredPricingModal";
import { ImportNonCoveredPricingModal } from "../components/ImportNonCoveredPricingModal";
import { NonCoveredPricingActionCell } from "../components/NonCoveredPricingActionCell";

const useColumns = makeColumns(() => [
  {
    id: "cpt_code.code",
    accessor: "cptCode.code",
    Header: "CPT Code",
    Cell: TokenCell,
    Filter: (props) => <AdministrateEnumFilter {...props} resourceUrl="/admin/cpt_codes.json" valueKey="code" labelKey="code" />,
  },
  {
    id: "price_cents",
    accessor: "priceCents",
    Header: "Price",
    Cell: ({ value }) => <span>{formatCentsAsDollars(value)}</span>,
    disableFilters: true,
  },
  {
    id: "payor",
    accessor: "payor.name",
    Header: "Payor",
    disableFilters: true,
  },
  {
    id: "plan_type",
    accessor: "planTypeLabel",
    Header: "Plan Type",
    disableFilters: true,
  },
  {
    id: "actions",
    Header: "Actions",
    Cell: NonCoveredPricingActionCell,
    disableFilters: true,
    disableSortBy: true,
  },
]);

export const NonCoveredPricingTab = ({ labId }) => {
  const columns = useColumns();

  const extraParams = useMemo(
    () => ({
      lab: {
        id: labId,
      },
    }),
    [labId]
  );

  const { tableRef, allFilters, setTableFilters } = useAdministrateTable(extraParams);
  const [canCreate] = useCreatePermission("NonCoveredPricing");

  const onCreate = (newNonCoveredPricing) => {
    const { refresh, setMessage } = tableRef.current;

    setMessage(`Created Non-Covered Price for CPT Code ${newNonCoveredPricing.cpt_code.code}`);
    refresh();
  };

  const onImport = (message, details = []) => {
    const { refresh, setMessage } = tableRef.current;

    setMessage(message, details.length ? "warning" : "success", details);
    refresh();
  };

  return (
    <SystemConfigLayout title="Non-Covered Code Pricing">
      <Toolbar className="align-items-center mb-3">
        <Toolbar className="ms-auto">
          <AdministrateBackgroundExportButton
            outline
            className="me-2"
            exportUrl="/admin/non_covered_pricings/export"
            filters={allFilters}
            ref={tableRef}
          />

          {canCreate && (
            <>
              <ImportNonCoveredPricingModal outline className="me-2" onImport={onImport} labId={labId} />
              <CreateNonCoveredPricingModal onCreate={onCreate} labId={labId} />
            </>
          )}
        </Toolbar>
      </Toolbar>

      <AdministrateTable
        resourceUrl={`/admin/non_covered_pricings.json`}
        extraParams={extraParams}
        resourceName={"non_covered_pricings"}
        permissions={[UPDATE, DESTROY]}
        onFilteredChange={setTableFilters}
        columns={columns}
        ref={tableRef}
      />
    </SystemConfigLayout>
  );
};
